// What a screen module exports (ADR-032, user-flows §1): its routes, each tied to a NAV node and
// the requirement the navigation filters on. TASK-140 concatenates every module's `routes`.
import type { ComponentType } from "preact";
import type { Requirement } from "../session/session";

export type NavId =
  | "NAV-002"
  | "NAV-003"
  | "NAV-004"
  | "NAV-005"
  | "NAV-006"
  | "NAV-007"
  | "NAV-008"
  | "NAV-009"
  | "NAV-010"
  | "NAV-011"
  | "NAV-012"
  | "NAV-019";

/** Navigation labels (the redesign's plain words: Home → Overview, Orgs → Customers, …). */
export const NAV_LABELS: Readonly<Record<NavId, string>> = {
  "NAV-002": "Overview",
  "NAV-003": "Customers",
  "NAV-004": "Model prices",
  "NAV-005": "Invoices",
  "NAV-006": "Rate plans",
  "NAV-007": "Revenue & margin",
  "NAV-008": "AWS costs",
  "NAV-009": "Reconciliation",
  "NAV-010": "Installer",
  "NAV-011": "Audit log",
  "NAV-012": "Help",
  "NAV-019": "Quotas",
};

export interface NavGroup {
  /** Section heading in the sidebar; null for the ungrouped first and last items. */
  label: string | null;
  items: readonly NavId[];
}

/** The sidebar, grouped by job, in display order. Every NavId appears exactly once. */
export const NAV_GROUPS: readonly NavGroup[] = [
  { label: null, items: ["NAV-002"] },
  { label: "Customers", items: ["NAV-003", "NAV-019"] },
  { label: "Billing", items: ["NAV-005", "NAV-006", "NAV-004"] },
  { label: "Finance", items: ["NAV-007", "NAV-008", "NAV-009"] },
  { label: "Operations", items: ["NAV-010", "NAV-011"] },
  { label: null, items: ["NAV-012"] },
];

/** Every NavId in sidebar order. */
export const NAV_ORDER: readonly NavId[] = NAV_GROUPS.flatMap((group) => group.items);

/** The sidebar section a node sits in (for the breadcrumb), or null for an ungrouped one. */
export function navGroupLabel(nav: NavId): string | null {
  return NAV_GROUPS.find((group) => group.items.includes(nav))?.label ?? null;
}

/** `/orgs/:orgId/:tab` → `{ orgId: string; tab: string }`. */
export type RouteParams<P extends string> = P extends `${string}:${infer Param}/${infer Rest}`
  ? { [K in Param]: string } & RouteParams<`/${Rest}`>
  : P extends `${string}:${infer Param}`
    ? { [K in Param]: string }
    : Record<never, never>;

export interface ScreenProps<P extends string = string> {
  params: RouteParams<P>;
  query: URLSearchParams;
}

interface RouteFields {
  nav: NavId;
  path: string;
  /** Document title and heading of the screen. */
  title: string;
  permission: Requirement;
}

/** A route as a screen module exports it and the route table holds it (`routes: ScreenRoute[]`). */
export interface ScreenRoute extends RouteFields {
  component: ComponentType<{ params: Record<string, string>; query: URLSearchParams }>;
}

/** A route whose component is typed by its own path's parameters. */
export interface TypedRoute<P extends string> extends RouteFields {
  path: P;
  component: ComponentType<ScreenProps<P>>;
}

/** Checks the component against the path's parameters, then erases the path for the table. */
export function defineRoute<P extends string>(route: TypedRoute<P>): ScreenRoute {
  return route as unknown as ScreenRoute;
}
